import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Button as MuiButton,
  FormLabel,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment
} from "@material-ui/core";
import {
  HelpOutline as HelpOutlineIcon,
  EmailOutlined as EmailOutlinedIcon,
  Search as SearchIcon
} from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import styled from "styled-components";
import CircularProgress from "@material-ui/core/CircularProgress";
import SchoolGroupService from "../../../services/SchoolGroupService";
import Button from "../../../waybee-ui/Button";
import ReceiversSelector from "./ReceiversSelector";
import ResponsibleReceiversSelector from "./ResponsibleReceiversSelector";
import ReceiversList from "./ReceiversList";
import {
  areAllDependentsMarked,
  buildResponsibleStudentLinks as resolveResponsibleStudentLinks,
  getDefaultDependentChecked,
  getRecipientsSummary
} from "../utils/responsibleStudentLinks";

const SelectAllButton = styled(MuiButton)`
  text-decoration: underline;
  text-transform: capitalize;
  color: rgba(0, 0, 0, 0.5);
  font-size: 14px;
  white-space: nowrap;
`;

const ScrollableDialogContent = styled(DialogContent)`
  && {
    max-height: 70vh;
    overflow-y: auto;
    padding-top: 0;
  }
`;

const EMAIL_HELP_TEXT =
  "Ao selecionar os destinatários manualmente, os usuários que não baixaram o app ou não realizaram o primeiro acesso não receberão o e-mail do comunicado.";

const StickySearch = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  padding: 12px;
  margin: 0 -24px 16px;
`;

const SearchField = styled(TextField)`
  && .MuiInputBase-root {
    font-size: 16px;
  }

  && .MuiOutlinedInput-input {
    padding: 14px 16px;
  }
`;

const GroupSubtitle = styled(Typography)`
  && {
    padding: 0 20px 12px;
    display: block;
  }
`;

const FooterSummary = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 24px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
`;

class DestinationModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      users: [],
      initialUsers: [],
      isLoading: false,
      helperText: "",
      searchTerm: ""
    };
  }

  isCpfRecipientList = () => {
    const { simpleFilter } = this.props;
    const { destinationType, selectedRecipientListType } = simpleFilter;
    return (
      destinationType === "recipientList" && selectedRecipientListType === "CPF"
    );
  };

  getDestinationUser = (excludedIdsList, users) => {
    return users.map(user => {
      let checked = true;
      excludedIdsList.forEach(id => {
        if (parseFloat(id) === user.id) {
          checked = false;
        }
      });

      const destinationUser = {
        photoProfile: user.photoProfile,
        studentId: user.studentId,
        name: user.name,
        id: user.id,
        checked,
        typeUser: user.typeUser
      };

      if (user.typeUser === "sponsor" && user.dependents) {
        destinationUser.dependents = user.dependents.map(dependent => ({
          id: dependent.id,
          name: dependent.name,
          photoProfile: dependent.photoProfile,
          studentId: dependent.studentId,
          checked: getDefaultDependentChecked(user.dependents)
        }));
      }

      return destinationUser;
    });
  };

  mergeSavedSubscribers = (users, savedSubscribers) => {
    if (!savedSubscribers || !savedSubscribers.length) {
      return users;
    }

    return users.map(user => {
      const savedUser = savedSubscribers.find(subscriber => subscriber.id === user.id);
      if (!savedUser) {
        return user;
      }

      const mergedUser = {
        ...user,
        checked: savedUser.checked
      };

      if (user.dependents && savedUser.dependents) {
        mergedUser.dependents = user.dependents.map(dependent => {
          const savedDependent = savedUser.dependents.find(
            savedDep => savedDep.id === dependent.id
          );
          return savedDependent
            ? { ...dependent, checked: savedDependent.checked }
            : dependent;
        });
      }

      return mergedUser;
    });
  };

  applyResponsibleStudentLinks = (users, responsibleStudentLinks) => {
    if (!responsibleStudentLinks?.length) {
      return users;
    }

    return users.map(user => {
      if (user.typeUser !== "sponsor") {
        return user;
      }

      const links = responsibleStudentLinks.filter(link => link.userId === user.id);
      if (!links.length) {
        return user;
      }

      return {
        ...user,
        dependents: (user.dependents || []).map(dependent => ({
          ...dependent,
          checked: links.some(link => link.studentId === dependent.id)
        }))
      };
    });
  };

  loadUsers = async () => {
    this.setState({ isLoading: true });
    const { simpleFilter, advancedFilter, savedSubscribers, responsibleStudentLinks } = this.props;
    const {
      destinationType,
      employeeClasses,
      employeeCourses,
      employeeTypes,
      studentCourses,
      studentClasses,
      excludedIds,
      recipientListValues,
      selectedRecipientListType
    } = simpleFilter;

    const studentCourseIds = studentCourses.map(e => e.CODIGO);
    const studentClassIds = studentClasses.map(e => e.CODIGO);

    const employeeCourseIds = employeeCourses.map(e => e.CODIGO);
    const employeeClassIds = employeeClasses.map(e => e.CODIGO);
    const employeeTypesIds = employeeTypes.map(e => e.CODIGO);

    let destinationUser = [];
    let filteredUsers = [];

    if (destinationType === "employees") {
      destinationUser = await SchoolGroupService.getDestinationEmployees(
        employeeCourseIds,
        employeeClassIds,
        employeeTypesIds
      );
    } else if (destinationType === "recipientList" && recipientListValues) {
      let studentsIds = [];
      let studentsDocumentNumber = [];

      if (selectedRecipientListType === "RA") {
        studentsIds = recipientListValues;
      } else if (selectedRecipientListType === "CPF") {
        studentsDocumentNumber = recipientListValues;
      }

      destinationUser = await SchoolGroupService.getDestinationUsersByRecipientList(
        studentsIds,
        studentsDocumentNumber
      );
    } else if (destinationType !== "employees" && studentCourseIds.length) {
      destinationUser = await SchoolGroupService.getDestinationUsers(
        studentCourseIds,
        studentClassIds,
        advancedFilter
      );
    }

    let excludedIdsList = [];
    if (excludedIds) excludedIdsList = excludedIds.split(",");
    filteredUsers = this.getDestinationUser(excludedIdsList, destinationUser);
    filteredUsers = this.mergeSavedSubscribers(filteredUsers, savedSubscribers);
    filteredUsers = this.applyResponsibleStudentLinks(
      filteredUsers,
      responsibleStudentLinks
    );

    this.setState({
      users: filteredUsers,
      initialUsers: this.cloneUsers(filteredUsers),
      isLoading: false,
      helperText: "",
      searchTerm: ""
    });
  };

  handleOpen = () => {
    this.loadUsers();
  };

  handleClose = () => {
    const { initialUsers } = this.state;
    const { onClose } = this.props;
    onClose();
    this.searchByName("");
    this.setState({
      users: this.cloneUsers(initialUsers),
      searchTerm: ""
    });
  };

  handleSubmit = () => {
    const { setSubscribers, onClose, isConfirm, onConfirm } = this.props;
    const { initialUsers } = this.state;

    const excludedDestinationIds = initialUsers
      .filter(e => e.checked === false)
      .map(e => e.id)
      .toString();

    if (isConfirm) {
      const confirmData = {
        users: initialUsers,
        excludedDestinationIds
      };

      if (this.isCpfRecipientList()) {
        confirmData.responsibleStudentLinks = resolveResponsibleStudentLinks(
          initialUsers
        );
      }

      return onConfirm(confirmData);
    }

    setSubscribers("studentSubscribers", initialUsers);
    setSubscribers("excludedDestinationIds", excludedDestinationIds);

    if (this.isCpfRecipientList()) {
      setSubscribers(
        "responsibleStudentLinks",
        resolveResponsibleStudentLinks(initialUsers)
      );
    }

    return onClose();
  };

  cloneUsers = users => {
    return JSON.parse(JSON.stringify(users));
  };

  updateUsersState = updater => {
    this.setState(prevState => {
      const newInitialUsers = updater(prevState.initialUsers);
      const newUsers = updater(prevState.users);
      return {
        users: newUsers,
        initialUsers: newInitialUsers
      };
    });
  };

  handleToggle = (id, checked) => {
    this.updateUsersState(users =>
      users.map(user => {
        if (user.id !== id) {
          return user;
        }

        const updatedUser = { ...user, checked };
        if (user.typeUser === "sponsor" && user.dependents) {
          updatedUser.dependents = user.dependents.map(dependent => ({
            ...dependent,
            checked: checked ? dependent.checked : false
          }));
        }
        return updatedUser;
      })
    );
  };

  handleToggleDependent = (responsibleId, dependentId, checked) => {
    this.updateUsersState(users =>
      users.map(user => {
        if (user.id !== responsibleId) {
          return user;
        }

        return {
          ...user,
          dependents: (user.dependents || []).map(dependent =>
            dependent.id === dependentId
              ? { ...dependent, checked }
              : dependent
          )
        };
      })
    );
  };

  selectAll = (users, cheked) => {
    this.updateUsersState(() =>
      users.map(user => ({
        ...user,
        checked: cheked,
        dependents: (user.dependents || []).map(dependent => ({
          ...dependent,
          checked: cheked ? dependent.checked : false
        }))
      }))
    );
  };

  selectAllDependents = () => {
    const { initialUsers } = this.state;
    const shouldMarkAll = !areAllDependentsMarked(initialUsers);

    this.updateUsersState(users =>
      users.map(user => {
        if (user.typeUser !== "sponsor" || !user.checked) {
          return user;
        }

        return {
          ...user,
          dependents: (user.dependents || []).map(dependent => ({
            ...dependent,
            checked: shouldMarkAll
          }))
        };
      })
    );
  };

  searchByName = value => {
    const { initialUsers } = this.state;
    const filterValue = value.trim().toLowerCase();

    if (!filterValue) {
      this.setState({
        users: initialUsers,
        searchTerm: value,
        helperText: ""
      });
      return;
    }

    const searchedUsers = initialUsers.filter(user => {
      const userName = user.name.toLowerCase();
      const userRa = (user.studentId || "").toLowerCase();
      const dependentsMatch = (user.dependents || []).some(dependent => {
        const dependentName = dependent.name.toLowerCase();
        const dependentRa = (dependent.studentId || "").toLowerCase();
        return (
          dependentName.includes(filterValue) || dependentRa.includes(filterValue)
        );
      });

      return (
        userName.includes(filterValue) ||
        userRa.includes(filterValue) ||
        dependentsMatch
      );
    });

    this.setState({
      users: searchedUsers,
      searchTerm: value,
      helperText:
        searchedUsers.length === 0 ? "Nenhum usuário encontrado" : ""
    });
  };

  hasValidRecipients = () => {
    const { initialUsers } = this.state;
    return initialUsers.some(user => user.checked);
  };

  renderUsers = checkedUser => {
    const { users } = this.state;
    const { isConfirm } = this.props;

    if (isConfirm) return checkedUser;

    return users;
  };

  render() {
    const { users, isLoading, helperText, searchTerm, initialUsers } = this.state;
    const {
      isOpen,
      isConfirm,
      confirmBtnText,
      isResend,
      filterHelpText,
      sentEmail
    } = this.props;

    const isCpfRecipientList = this.isCpfRecipientList();
    const sourceUsers = searchTerm ? users : initialUsers;
    const responsibleUsers = sourceUsers.filter(
      user =>
        user.typeUser === "sponsor" &&
        user.dependents?.length &&
        (!isConfirm || user.checked)
    );
    const studentUsers = sourceUsers.filter(user => user.typeUser !== "sponsor");
    const unCheckedUser = users.filter(e => !e.checked);
    const checkedUser = users.filter(e => e.checked);
    const countCheckedUsers = initialUsers.filter(e => e.checked).length;
    const hasValidRecipients = this.hasValidRecipients();
    const allDependentsMarked = areAllDependentsMarked(initialUsers);
    const recipientsSummary = getRecipientsSummary(initialUsers, isCpfRecipientList);
    const showGroupSubtitle = !isConfirm;
    const cpfStudentDisplayUsers = isConfirm
      ? studentUsers.filter(user => user.checked)
      : studentUsers;
    const hasResponsibleInList = initialUsers.some(
      user => user.typeUser === "sponsor" && user.dependents?.length
    );
    const hasStudentInList = initialUsers.some(user => user.typeUser !== "sponsor");
    const displayStudentUsers = isCpfRecipientList
      ? cpfStudentDisplayUsers
      : this.renderUsers(checkedUser);

    const title = isConfirm ? "Confirmar " : "Gerenciar ";

    return (
      <Dialog
        onClose={this.handleClose}
        open={isOpen}
        onEnter={this.handleOpen}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle onClose={this.handleClose}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="span">
              {isConfirm && isResend ? "Novos " : title}
              {" "}
              destinatários
            </Typography>
            <Box display="flex" alignItems="center">
              {filterHelpText && (
                <Tooltip title={filterHelpText} placement="left">
                  <IconButton size="small" aria-label="Ajuda sobre destinatários">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {sentEmail && (
                <Tooltip title={EMAIL_HELP_TEXT} placement="left">
                  <IconButton size="small" aria-label="Ajuda sobre envio por e-mail">
                    <EmailOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <ScrollableDialogContent dividers>
          <Box mt={1}>
            {isCpfRecipientList && (hasResponsibleInList || hasStudentInList) && (
              <Alert severity="info">
                <Typography component="div">
                  {hasResponsibleInList && (
                    <>
                      Para cada responsável, indique a quais alunos o comunicado
                      se refere. Se nenhum for marcado, todos os dependentes são considerados.
                      Apenas o responsável receberá o comunicado.
                    </>
                  )}
                  {hasResponsibleInList && hasStudentInList && (
                    <>
                      <br />
                      <br />
                    </>
                  )}
                  {hasStudentInList && (
                    <>
                      Alunos cujo CPF está diretamente na lista recebem o comunicado.
                    </>
                  )}
                </Typography>
              </Alert>
            )}

            {isResend && (
              <Typography variant="subtitle1">
                Lista de destinatários de cada grupo que vão receber essa cópia
              </Typography>
            )}
          </Box>

          <StickySearch>
            <SearchField
              fullWidth
              variant="outlined"
              placeholder="Buscar por nome ou RA do aluno ou responsável"
              onChange={e => this.searchByName(e.target.value)}
              helperText={helperText}
              value={searchTerm}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </StickySearch>

          <Box pb={2}>
            {isLoading && <CircularProgress />}

            {!isLoading && users.length > 0 && (
              <>
                {(responsibleUsers.length > 0 || displayStudentUsers.length > 0) && (
                  <>
                    {showGroupSubtitle && (
                      <GroupSubtitle variant="body2" color="textSecondary">
                        Desmarque os remetentes de cada grupo que NÃO receberão
                        esta comunicação
                      </GroupSubtitle>
                    )}
                    {responsibleUsers.length > 0 && (
                      <ResponsibleReceiversSelector
                        users={responsibleUsers}
                        disabledResponsible={isConfirm}
                        disabledDependents={isConfirm}
                        showStudentLabel={isCpfRecipientList}
                        onToggleUser={(id, check) =>
                          this.handleToggle(id, check)
                        }
                        onToggleDependent={(responsibleId, dependentId, check) =>
                          this.handleToggleDependent(
                            responsibleId,
                            dependentId,
                            check
                          )
                        }
                      />
                    )}
                    {displayStudentUsers.length > 0 && (
                      <ReceiversSelector
                        isResend={isResend}
                        users={displayStudentUsers}
                        disabled={isConfirm}
                        showStudentLabel={isCpfRecipientList}
                        onToggleUser={(id, check) => this.handleToggle(id, check)}
                        isLoading={isLoading}
                      />
                    )}
                  </>
                )}

                {isConfirm && unCheckedUser.length > 0 && (
                  <Box my={1} mb={3} mt={2}>
                    <FormLabel>
                      Destinatários retirados da comunicação
                    </FormLabel>
                    <ReceiversList users={unCheckedUser} />
                  </Box>
                )}
              </>
            )}
          </Box>
        </ScrollableDialogContent>

        <FooterSummary>
          <Typography variant="body2" color="textSecondary">
            {recipientsSummary}
          </Typography>
          {isCpfRecipientList &&
            responsibleUsers.length > 0 &&
            !searchTerm &&
            !isConfirm && (
              <SelectAllButton onClick={this.selectAllDependents}>
                {allDependentsMarked
                  ? "Desmarcar todos dependentes"
                  : "Marcar todos dependentes"}
              </SelectAllButton>
            )}
          {!isCpfRecipientList &&
            !isConfirm &&
            !searchTerm &&
            users.length > 0 && (
              <SelectAllButton
                onClick={() =>
                  this.selectAll(users, countCheckedUsers < users.length)
                }
              >
                {countCheckedUsers === users.length
                  ? "Desmarcar Todos"
                  : "Marcar Todos"}
              </SelectAllButton>
            )}
        </FooterSummary>
        <DialogActions>
          <Button
            variant="contained"
            autoFocus
            onClick={this.handleClose}
            color="secondary"
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            autoFocus
            onClick={this.handleSubmit}
            color="primary"
            disabled={!hasValidRecipients}
          >
            {confirmBtnText}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

DestinationModal.defaultProps = {
  advancedFilter: {},
  isConfirm: false,
  onConfirm: null,
  isResend: false,
  confirmBtnText: "Salvar",
  savedSubscribers: [],
  responsibleStudentLinks: [],
  filterHelpText: null,
  sentEmail: false
};

DestinationModal.propTypes = {
  simpleFilter: PropTypes.shape({
    studentClasses: PropTypes.arrayOf(
      PropTypes.shape({
        CODIGO: PropTypes.string
      })
    ).isRequired,
    studentCourses: PropTypes.arrayOf(
      PropTypes.shape({
        CODIGO: PropTypes.string
      })
    ).isRequired,
    employeeTypes: PropTypes.arrayOf(
      PropTypes.shape({
        CODIGO: PropTypes.string
      })
    ).isRequired,
    employeeClasses: PropTypes.arrayOf(
      PropTypes.shape({
        CODIGO: PropTypes.string
      })
    ).isRequired,
    employeeCourses: PropTypes.arrayOf(
      PropTypes.shape({
        CODIGO: PropTypes.string
      })
    ).isRequired,
    excludedIds: PropTypes.string,
    destinationType: PropTypes.string,
    recipientListValues: PropTypes.string,
    selectedRecipientListType: PropTypes.string
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setSubscribers: PropTypes.func.isRequired,
  advancedFilter: PropTypes.shape({}),
  isConfirm: PropTypes.bool,
  onConfirm: PropTypes.func,
  isResend: PropTypes.bool,
  confirmBtnText: PropTypes.string,
  filterHelpText: PropTypes.string,
  sentEmail: PropTypes.bool,
  savedSubscribers: PropTypes.arrayOf(PropTypes.shape({})),
  responsibleStudentLinks: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.number,
      studentId: PropTypes.number
    })
  )
};

export default DestinationModal;
